import type { z } from "zod/v4";
import type { VizAuthoringIssue } from "@/lib/ai/visualization/types";
import type { VizSpec } from "@/lib/ai/visualization/views/compiled-spec";
import type {
  BuildVizElementArgs,
  BuiltVizElement,
} from "@/lib/ai/visualization/views/shared/element-builder";
import { buildSeriesLabelMap } from "@/lib/ai/visualization/views/shared/label-map";
import {
  buildSpecValidationIssue,
  collectSeriesValues,
  pushDuplicateValueIssues,
  pushMappedKeyIssues,
  pushProjectableMetricBindingIssue,
  pushRequiredMappedKeyCoverageIssues,
  pushRequiredTextIssue,
  pushSelectedKeyIssues,
  validateFieldRole,
} from "@/lib/ai/visualization/views/shared/spec-validation";
import type {
  ValidateVizSpecArgs,
  VizAuthoringCardDraft,
} from "@/lib/ai/visualization/views/shared/view-definition";
import type { timeSeriesGroupedLineAuthoringSchema } from "@/lib/ai/visualization/views/time-series-grouped-line/schema";

type TimeSeriesGroupedLineSpec = Extract<
  VizSpec,
  { type: "time-series-grouped-line" }
>;
type TimeSeriesGroupedLineAuthoring = z.infer<
  typeof timeSeriesGroupedLineAuthoringSchema
>;

/**
 * grouped time-series spec が、1 つの group dimension と 1 つ以上の metric を
 * 使う strict contract に収まっているかを検証する。
 *
 * @remarks
 * compare dataset や empty grouped dataset をここで止めて、projection 側で
 * synthetic series を無理に捏造しない。
 */
export function validateTimeSeriesGroupedLineSpec(
  args: ValidateVizSpecArgs<TimeSeriesGroupedLineSpec>,
): VizAuthoringIssue[] {
  const issues: VizAuthoringIssue[] = [];
  validateFieldRole({
    issues,
    dataset: args.dataset,
    path: "spec.xKey",
    fieldName: args.spec.xKey,
    role: "time-dimension",
  });
  validateFieldRole({
    issues,
    dataset: args.dataset,
    path: "spec.groupKey",
    fieldName: args.spec.groupKey,
    role: "dimension",
  });
  if (args.spec.xKey === args.spec.groupKey) {
    issues.push(
      buildSpecValidationIssue({
        path: "spec.groupKey",
        code: "duplicate-dimension",
        message: "groupKey must differ from xKey",
      }),
    );
  }

  if (args.dataset.profile.compareMode !== "none") {
    issues.push(
      buildSpecValidationIssue({
        path: "selectedDataset.profile.compareMode",
        code: "unsupported-compare-mode",
        message:
          "time-series-grouped-line does not support compare-mode datasets",
      }),
    );
  }

  pushDuplicateValueIssues({
    issues,
    path: "spec.metricKeys",
    values: args.spec.metricKeys,
  });
  args.spec.metricKeys.forEach((metricKey, index) => {
    validateFieldRole({
      issues,
      dataset: args.dataset,
      path: `spec.metricKeys[${index}]`,
      fieldName: metricKey,
      role: "metric",
    });
  });
  pushProjectableMetricBindingIssue({
    issues,
    dataset: args.dataset,
    path: "spec.groupKey",
    categoryKeys: [args.spec.xKey, args.spec.groupKey],
    metricKeys: args.spec.metricKeys,
  });

  const realizedGroups = collectSeriesValues(args.dataset, args.spec.groupKey);
  if (realizedGroups.length === 0) {
    issues.push(
      buildSpecValidationIssue({
        path: "spec.groupKey",
        code: "insufficient-groups",
        message:
          "time-series-grouped-line requires at least one realized group value",
      }),
    );
  }

  const allowedMetricKeys = new Set(args.spec.metricKeys);
  pushRequiredTextIssue({
    issues,
    path: "spec.componentProps.xLabel",
    value: args.spec.componentProps?.xLabel ?? null,
    message:
      "xLabel is required because chart axis labels are owned by the authoring output",
  });
  pushMappedKeyIssues({
    issues,
    path: "spec.componentProps.seriesLabels",
    entries: args.spec.componentProps?.seriesLabels,
    allowedKeys: allowedMetricKeys,
    emptyAllowedMessage:
      "seriesLabels requires at least one metric key in spec.metricKeys",
  });
  pushRequiredMappedKeyCoverageIssues({
    issues,
    path: "spec.componentProps.seriesLabels",
    entries: args.spec.componentProps?.seriesLabels,
    requiredKeys: args.spec.metricKeys,
    emptyMessage:
      "seriesLabels must include every selected metric key because metric labels are owned by the authoring output",
  });

  pushSelectedKeyIssues({
    issues,
    path: "spec.componentProps.defaultVisibleSeriesKeys",
    selectedKeys: args.spec.componentProps?.defaultVisibleSeriesKeys ?? [],
    allowedKeys: new Set(realizedGroups),
    emptyAllowedMessage:
      "defaultVisibleSeriesKeys requires realized group values",
  });

  return issues;
}

/**
 * structured authoring payload を grouped time-series 用の renderer-ready spec へ変換する。
 *
 * @remarks
 * realized synthetic series は projection 後に確定するため、compile 時点では空配列で
 * 持ち、`projectDatasetForRender` が最終 series metadata を埋める。
 */
export function compileTimeSeriesGroupedLineSpec(args: {
  datasetId: string;
  dataset: BuildVizElementArgs<TimeSeriesGroupedLineSpec>["dataset"];
  card: VizAuthoringCardDraft | null;
  view: TimeSeriesGroupedLineAuthoring;
}): TimeSeriesGroupedLineSpec {
  return {
    type: "time-series-grouped-line",
    datasetId: args.datasetId,
    cardTitle: args.card?.title ?? null,
    xKey: args.view.xKey,
    groupKey: args.view.groupKey,
    metricKeys: args.view.metricKeys,
    series: [],
    componentProps: {
      xLabel: args.view.xLabel,
      seriesLabels: buildSeriesLabelMap({
        seriesKeys: args.view.metricKeys,
        entries: args.view.metricLabelEntries,
      }),
    },
  };
}

/**
 * grouped time-series 用の UI element を構築する。
 *
 * @remarks
 * element builder は projection 後の realized series をそのまま renderer へ渡し、
 * raw group rows の再解釈を client 側へ漏らさない。
 */
export function buildTimeSeriesGroupedLineElement(
  args: BuildVizElementArgs<TimeSeriesGroupedLineSpec>,
): BuiltVizElement {
  return {
    elementKey: args.chartKey,
    element: {
      type: "TimeSeriesGroupedLine",
      props: {
        dataPath: args.dataPath,
        xKey: args.spec.xKey,
        series: args.spec.series,
        ...(args.spec.componentProps ?? {}),
      },
      children: [],
    },
  };
}
